import type { Tenant } from "components/TenantPicker";

import BoltIcon from "@mui/icons-material/Bolt";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { TenantPickerDialog } from "components/TenantPickerDialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { bolt, BoltEventType } from "utils/metricsLogger";

import type { ScoutChatMessage } from "../../Customer/Scout/Chat/message/scoutChatMessage";

import { AgentMessage } from "../../Customer/Scout/Chat/message/AgentMessage";
import { UserMessage } from "../../Customer/Scout/Chat/message/UserMessage";
import { useScoutConversation } from "../../Customer/Scout/hooks/useScoutConversation";

const DEFAULT_LOAD_TEST_CLIENT_COUNT = 10;
const MIN_LOAD_TEST_CLIENT_COUNT = 10;
const MAX_LOAD_TEST_CLIENT_COUNT = 1000;
const MAX_TOKEN_RENDERING_CLIENT_COUNT = 8;
const TOKEN_RENDERING_DISABLED_TOOLTIP =
  "Token rendering is disabled for higher client counts to avoid performance issues";
const CLIENT_COUNT_DEBOUNCE_MS = 1000;
const COMPACT_STREAMING_QUADRANT_HEIGHT = 400;
const RETIRE_ABORT_TIMEOUT_MS = 15000;
const INITIAL_PROMPT = "Write 5 poems about EPRCA and RCRA in different styles";
const REPEAT_PROMPT = "Write five more in five new styles";
const MAX_MESSAGES_PER_CONVERSATION = 5;
const TURN_FINISHED_SETTLE_DELAY_MS = 2500;
const ALREADY_EXECUTING_RETRY_DELAY_MS = 10000;

type ScoutLoadTestQuadrantProps = {
  index: number;
  tenantId: string;
  renderTokens: boolean;
  fillAvailableSpace: boolean;
  retiring: boolean;
  onRetired: (index: number) => void;
};

type ScoutLoadTestQuadrantCycleProps = ScoutLoadTestQuadrantProps & {
  cycleId: number;
  onCycleComplete: () => void;
};

type SendLoopState = "ready" | "sending" | "error";
type ActiveTurnSnapshot = { requestId: string; status: string | undefined };

function isTurnRunning(status: string | undefined) {
  return status === "starting" || status === "streaming";
}

function isTurnTerminal(status: string | undefined) {
  return status === "finished" || status === "aborted" || status === "error";
}

function isAlreadyExecutingTurnError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes("already executing a turn");
}

function createClientIndexes(count: number) {
  return Array.from({ length: count }, (_value, index) => index);
}

function getClientCountSliderValue(value: number | number[]) {
  return Array.isArray(value) ? value[0] : value;
}

function getQuadrantStatus({
  activeTurnStatus,
  isSending,
  retiring,
}: {
  activeTurnStatus: string | undefined;
  isSending: boolean;
  retiring: boolean;
}) {
  if (retiring) {
    return "shutting down";
  }

  if (activeTurnStatus) {
    return activeTurnStatus;
  }

  return isSending ? "starting" : "idle";
}

function ScoutLoadTestQuadrant({
  index,
  tenantId,
  renderTokens,
  fillAvailableSpace,
  retiring,
  onRetired,
}: ScoutLoadTestQuadrantProps) {
  const [cycleId, setCycleId] = useState(1);
  const handleCycleComplete = useCallback(() => {
    setCycleId((currentCycleId) => currentCycleId + 1);
  }, []);

  return (
    <ScoutLoadTestQuadrantCycle
      key={`${index}-${cycleId}`}
      index={index}
      tenantId={tenantId}
      renderTokens={renderTokens}
      fillAvailableSpace={fillAvailableSpace}
      retiring={retiring}
      onRetired={onRetired}
      cycleId={cycleId}
      onCycleComplete={handleCycleComplete}
    />
  );
}

function ScoutLoadTestQuadrantCycle({
  index,
  tenantId,
  renderTokens,
  fillAvailableSpace,
  retiring,
  onRetired,
  cycleId,
  onCycleComplete,
}: ScoutLoadTestQuadrantCycleProps) {
  const didSendInitialPromptRef = useRef(false);
  const lastHandledFinishedRequestIdRef = useRef<string | undefined>(undefined);
  const settlingFinishedRequestIdRef = useRef<string | undefined>(undefined);
  const previousActiveTurnRef = useRef<ActiveTurnSnapshot | undefined>(undefined);
  const latestActiveTurnRef = useRef<ActiveTurnSnapshot | undefined>(undefined);
  const successfulMessageCountRef = useRef(0);
  const sendInFlightRef = useRef(false);
  const hasStartedRetiringRef = useRef(false);
  const hasCompletedRetiringRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retireTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isMountedRef = useRef(true);
  const retiringRef = useRef(retiring);
  const [sendLoopState, setSendLoopState] = useState<SendLoopState>("ready");
  const [sendLoopError, setSendLoopError] = useState<string | undefined>();
  const {
    conversation,
    conversationId,
    messages,
    activeTurn,
    loading,
    isSending,
    isAborting,
    sendMessage,
    abortTurn,
  } = useScoutConversation({
    tenantId,
    conversationId: undefined,
  });
  const activeTurnRequestId = activeTurn?.requestId;
  const activeTurnStatus = activeTurn?.status;
  const status = getQuadrantStatus({ activeTurnStatus, isSending, retiring });
  const isRunning = isSending || isAborting || isTurnRunning(activeTurnStatus);
  const componentKey = `scout-load-test-${index + 1}`;

  const emitLoadTestEvent = useCallback(
    (action: string, vars: Record<string, unknown>) => {
      bolt.emit({
        eventType: BoltEventType.Action,
        userAction: `scout_load_test.${action}`,
        componentName: "ScoutLoadTestQuadrant",
        componentKey,
        reasonKeys: [action],
        varsJson: {
          instance: index + 1,
          tenantId,
          conversationId,
          activeTurnRequestId,
          activeTurnStatus,
          cycleId,
          successfulMessageCount: successfulMessageCountRef.current,
          isSending,
          isAborting,
          ...vars,
        },
      });
    },
    [
      activeTurnRequestId,
      activeTurnStatus,
      componentKey,
      conversationId,
      cycleId,
      index,
      isAborting,
      isSending,
      tenantId,
    ]
  );

  const completeRetirement = useCallback(
    (reason: string) => {
      if (hasCompletedRetiringRef.current || !isMountedRef.current) {
        return;
      }

      hasCompletedRetiringRef.current = true;
      if (retireTimeoutRef.current) {
        clearTimeout(retireTimeoutRef.current);
        retireTimeoutRef.current = undefined;
      }

      emitLoadTestEvent("client_retire_complete", { reason });
      onRetired(index);
    },
    [emitLoadTestEvent, index, onRetired]
  );

  const completeConversationCycle = useCallback(
    (reason: string) => {
      if (retiringRef.current || !isMountedRef.current) {
        return;
      }

      emitLoadTestEvent("conversation_cycle_complete", {
        reason,
        successfulMessageCount: successfulMessageCountRef.current,
      });
      emitLoadTestEvent("conversation_cycle_restart", {
        reason,
        nextCycleId: cycleId + 1,
        successfulMessageCount: successfulMessageCountRef.current,
      });
      onCycleComplete();
    },
    [cycleId, emitLoadTestEvent, onCycleComplete]
  );

  const sendLoadTestPrompt = useCallback(
    async (prompt: string, trigger: "initial" | "repeat" | "retry") => {
      if (retiringRef.current) {
        emitLoadTestEvent("send_skipped_retiring", { prompt, trigger });
        return;
      }

      const scheduleRetry = (reason: string, extraVars?: Record<string, unknown>) => {
        if (retiringRef.current) {
          emitLoadTestEvent("send_retry_skipped_retiring", {
            prompt,
            trigger,
            reason,
            ...extraVars,
          });
          return;
        }

        if (retryTimeoutRef.current) {
          emitLoadTestEvent("send_retry_already_scheduled", {
            prompt,
            trigger,
            reason,
            retryDelayMs: ALREADY_EXECUTING_RETRY_DELAY_MS,
            ...extraVars,
          });
          return;
        }

        emitLoadTestEvent("send_retry_scheduled", {
          prompt,
          trigger,
          reason,
          retryDelayMs: ALREADY_EXECUTING_RETRY_DELAY_MS,
          ...extraVars,
        });
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = undefined;
          if (retiringRef.current) {
            emitLoadTestEvent("send_retry_cancelled_retiring", { prompt, trigger });
            return;
          }
          void sendLoadTestPrompt(prompt, "retry");
        }, ALREADY_EXECUTING_RETRY_DELAY_MS);
      };

      const latestActiveTurn = latestActiveTurnRef.current;
      if (sendInFlightRef.current) {
        emitLoadTestEvent("send_skipped_in_flight", { prompt, trigger });
        return;
      }

      if (isTurnRunning(latestActiveTurn?.status)) {
        emitLoadTestEvent("send_skipped_active_turn", {
          prompt,
          trigger,
          latestActiveTurn,
        });
        if (trigger !== "initial") {
          scheduleRetry("active_turn_visible", { latestActiveTurn });
        }
        return;
      }

      sendInFlightRef.current = true;
      setSendLoopState("sending");
      setSendLoopError(undefined);
      emitLoadTestEvent("send_start", { prompt, trigger });

      try {
        const nextConversationId = await sendMessage(prompt);
        const nextSuccessfulMessageCount = successfulMessageCountRef.current + 1;
        successfulMessageCountRef.current = nextSuccessfulMessageCount;
        emitLoadTestEvent("send_success", {
          prompt,
          trigger,
          nextConversationId,
          successfulMessageCount: nextSuccessfulMessageCount,
        });
        if (isMountedRef.current) {
          setSendLoopState("ready");
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (isAlreadyExecutingTurnError(error)) {
          emitLoadTestEvent("send_already_executing_retry_scheduled", {
            prompt,
            trigger,
            errorMessage,
            retryDelayMs: ALREADY_EXECUTING_RETRY_DELAY_MS,
          });
          if (isMountedRef.current) {
            setSendLoopState("ready");
            setSendLoopError(undefined);
          }
          scheduleRetry("already_executing_error", { errorMessage });
          return;
        }

        emitLoadTestEvent("send_error", { prompt, trigger, errorMessage });
        if (isMountedRef.current) {
          setSendLoopState("error");
          setSendLoopError(errorMessage);
        }
      } finally {
        sendInFlightRef.current = false;
      }
    },
    [emitLoadTestEvent, sendMessage]
  );

  useEffect(() => {
    retiringRef.current = retiring;
  }, [retiring]);

  useEffect(() => {
    latestActiveTurnRef.current =
      activeTurnRequestId !== undefined
        ? {
            requestId: activeTurnRequestId,
            status: activeTurnStatus,
          }
        : undefined;
  }, [activeTurnRequestId, activeTurnStatus]);

  useEffect(() => {
    if (!retiring || hasStartedRetiringRef.current) {
      return;
    }

    hasStartedRetiringRef.current = true;
    retiringRef.current = true;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = undefined;
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
    emitLoadTestEvent("client_retire_started", {});

    const retire = async () => {
      if (isRunning) {
        retireTimeoutRef.current = setTimeout(() => {
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
          emitLoadTestEvent("client_retire_abort_timeout", {
            timeoutMs: RETIRE_ABORT_TIMEOUT_MS,
          });
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
          completeRetirement("abort_timeout");
        }, RETIRE_ABORT_TIMEOUT_MS);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
        emitLoadTestEvent("client_retire_abort_start", {});
        try {
          const abortSucceeded = await abortTurn();
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
          emitLoadTestEvent("client_retire_abort_success", { abortSucceeded });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
          emitLoadTestEvent("client_retire_abort_error", { errorMessage });
        }
      }

      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
      completeRetirement("abort_settled");
    };

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
    void retire();
  }, [abortTurn, completeRetirement, emitLoadTestEvent, isRunning, retiring]);

  useEffect(() => {
    if (!retiring || !hasStartedRetiringRef.current || !isTurnTerminal(activeTurnStatus)) {
      return;
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
    completeRetirement(`terminal_${activeTurnStatus}`);
  }, [activeTurnStatus, completeRetirement, retiring]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
      if (retireTimeoutRef.current) {
        clearTimeout(retireTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (retiring || didSendInitialPromptRef.current || isRunning || messages.length > 0) {
      return;
    }

    didSendInitialPromptRef.current = true;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
    void sendLoadTestPrompt(INITIAL_PROMPT, "initial");
  }, [isRunning, messages.length, retiring, sendLoadTestPrompt]);

  useEffect(() => {
    if (retiring) {
      return;
    }

    const previousActiveTurn = previousActiveTurnRef.current;
    const currentActiveTurn =
      activeTurnRequestId !== undefined
        ? {
            requestId: activeTurnRequestId,
            status: activeTurnStatus,
          }
        : undefined;

    previousActiveTurnRef.current = currentActiveTurn;

    if (currentActiveTurn?.status !== "finished") {
      return;
    }

    if (
      !previousActiveTurn ||
      previousActiveTurn.requestId !== currentActiveTurn.requestId ||
      !isTurnRunning(previousActiveTurn.status)
    ) {
      return;
    }

    if (
      lastHandledFinishedRequestIdRef.current === currentActiveTurn.requestId ||
      settlingFinishedRequestIdRef.current === currentActiveTurn.requestId
    ) {
      return;
    }

    settlingFinishedRequestIdRef.current = currentActiveTurn.requestId;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
    emitLoadTestEvent("repeat_settle_scheduled", {
      finishedTurn: currentActiveTurn,
      previousTurn: previousActiveTurn,
      settleDelayMs: TURN_FINISHED_SETTLE_DELAY_MS,
    });
    settleTimeoutRef.current = setTimeout(() => {
      settleTimeoutRef.current = undefined;
      if (retiringRef.current) {
        return;
      }
      if (settlingFinishedRequestIdRef.current === currentActiveTurn.requestId) {
        settlingFinishedRequestIdRef.current = undefined;
      }
      const latestActiveTurn = latestActiveTurnRef.current;
      if (
        latestActiveTurn?.requestId !== currentActiveTurn.requestId ||
        latestActiveTurn.status !== "finished"
      ) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
        emitLoadTestEvent("repeat_settle_cancelled", {
          finishedTurn: currentActiveTurn,
          latestActiveTurn,
        });
        return;
      }

      lastHandledFinishedRequestIdRef.current = currentActiveTurn.requestId;
      if (successfulMessageCountRef.current >= MAX_MESSAGES_PER_CONVERSATION) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
        completeConversationCycle("max_messages");
        return;
      }

      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-ref-to-parent
      void sendLoadTestPrompt(REPEAT_PROMPT, "repeat");
    }, TURN_FINISHED_SETTLE_DELAY_MS);
  }, [
    activeTurnRequestId,
    activeTurnStatus,
    completeConversationCycle,
    emitLoadTestEvent,
    retiring,
    sendLoadTestPrompt,
  ]);

  return (
    <Paper
      variant="outlined"
      sx={{
        minHeight: renderTokens
          ? { xs: COMPACT_STREAMING_QUADRANT_HEIGHT, md: fillAvailableSpace ? 0 : undefined }
          : 168,
        height:
          renderTokens && fillAvailableSpace
            ? "100%"
            : renderTokens
              ? COMPACT_STREAMING_QUADRANT_HEIGHT
              : "auto",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 1.5,
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <BoltIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" noWrap>
            Instance {index + 1}
          </Typography>
          <Chip label={status} size="small" variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary" noWrap title={conversation?.id}>
          {conversation?.id ?? conversationId ?? "Starting"}
        </Typography>
      </Stack>

      {sendLoopError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {sendLoopError}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ScoutLoadTestConversation
          messages={messages}
          loading={loading}
          isSending={isRunning || sendLoopState === "sending"}
          renderTokens={renderTokens}
        />
      </Box>
    </Paper>
  );
}

function ScoutLoadTestConversation({
  messages,
  loading,
  isSending,
  renderTokens,
}: {
  messages: ScoutChatMessage[];
  loading: boolean;
  isSending: boolean;
  renderTokens: boolean;
}) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const latestMessageKey = messages.at(-1)?.updatedAt ?? messages.at(-1)?.id;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    if (typeof scrollContainer.scrollTo === "function") {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: "auto",
      });
      return;
    }

    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [latestMessageKey, messages.length]);

  if (loading && messages.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%", color: "text.secondary", px: 2 }}
      >
        <Typography variant="body2">Loading conversation...</Typography>
      </Stack>
    );
  }

  if (!renderTokens) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%", color: "text.secondary", px: 2, textAlign: "center" }}
      >
        <Typography variant="body2">
          Token rendering paused. {messages.length} message{messages.length === 1 ? "" : "s"} in
          conversation.
        </Typography>
      </Stack>
    );
  }

  if (messages.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%", color: "text.secondary", px: 2, textAlign: "center" }}
      >
        <Typography variant="body2">
          {isSending ? "Starting Scout conversation..." : "Waiting to start..."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      ref={scrollContainerRef}
      aria-live="polite"
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Box sx={{ minWidth: 0, py: 1 }}>
        {messages.map((message) =>
          message.role === "user" ? (
            <UserMessage key={message.id} message={message} />
          ) : (
            <AgentMessage key={message.id} message={message} />
          )
        )}
      </Box>
    </Box>
  );
}

export function ScoutLoadTest() {
  const { tenantId, tenant, setCurrentTenantId } = useTenant();
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantIdFromSearchParams = searchParams.get("tenantId") ?? undefined;
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [renderTokens, setRenderTokens] = useState(true);
  const [selectedClientCount, setSelectedClientCount] = useState(DEFAULT_LOAD_TEST_CLIENT_COUNT);
  const [activeClientCount, setActiveClientCount] = useState(DEFAULT_LOAD_TEST_CLIENT_COUNT);
  const [retiringClientIndexes, setRetiringClientIndexes] = useState<number[]>([]);
  const renderedClientIndexes = useMemo(() => {
    const indexes = new Set([...createClientIndexes(activeClientCount), ...retiringClientIndexes]);
    return [...indexes].sort((leftIndex, rightIndex) => leftIndex - rightIndex);
  }, [activeClientCount, retiringClientIndexes]);
  const clientCountIsPending = selectedClientCount !== activeClientCount;
  const tokenRenderingDisabledForClientCount =
    selectedClientCount > MAX_TOKEN_RENDERING_CLIENT_COUNT;
  const effectiveRenderTokens = renderTokens && !tokenRenderingDisabledForClientCount;
  const fillAvailableSpace =
    effectiveRenderTokens && renderedClientIndexes.length <= DEFAULT_LOAD_TEST_CLIENT_COUNT;
  const scoutLoadTestEnabled = featureFlags[FeatureFlagKey.ScoutLoadTest];
  const loadTestTenantId =
    tenantId && scoutLoadTestEnabled && !featureFlagsLoading ? tenantId : undefined;

  const emitPageLoadTestEvent = useCallback(
    (action: string, vars: Record<string, unknown>) => {
      bolt.emit({
        eventType: BoltEventType.Action,
        userAction: `scout_load_test.${action}`,
        componentName: "ScoutLoadTest",
        componentKey: "scout-load-test",
        reasonKeys: [action],
        varsJson: {
          selectedClientCount,
          activeClientCount,
          ...vars,
        },
      });
    },
    [activeClientCount, selectedClientCount]
  );

  const handleTenantSelected = useCallback(
    (selectedTenant: Tenant) => {
      void setCurrentTenantId(selectedTenant.id);
      setSearchParams(
        (currentSearchParams) => {
          const nextSearchParams = new URLSearchParams(currentSearchParams);
          nextSearchParams.set("tenantId", selectedTenant.id);
          return nextSearchParams;
        },
        { replace: true }
      );
      setTenantPickerOpen(false);
    },
    [setCurrentTenantId, setSearchParams]
  );

  const handleClientCountSelected = useCallback(
    (_event: Event, value: number | number[]) => {
      const nextClientCount = getClientCountSliderValue(value);
      setSelectedClientCount(nextClientCount);
      if (nextClientCount > MAX_TOKEN_RENDERING_CLIENT_COUNT) {
        setRenderTokens(false);
      }
      emitPageLoadTestEvent("client_count_selected", {
        targetClientCount: nextClientCount,
      });
    },
    [emitPageLoadTestEvent]
  );

  const handleClientRetired = useCallback((index: number) => {
    setRetiringClientIndexes((currentIndexes) =>
      currentIndexes.filter((currentIndex) => currentIndex !== index)
    );
  }, []);

  useEffect(() => {
    if (!tenantIdFromSearchParams || tenantIdFromSearchParams === tenantId) {
      return;
    }

    void setCurrentTenantId(tenantIdFromSearchParams);
  }, [setCurrentTenantId, tenantId, tenantIdFromSearchParams]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveClientCount((currentActiveClientCount) => {
        if (currentActiveClientCount === selectedClientCount) {
          return currentActiveClientCount;
        }

        if (selectedClientCount < currentActiveClientCount) {
          setRetiringClientIndexes((currentIndexes) => {
            const indexes = new Set(currentIndexes);
            for (let index = selectedClientCount; index < currentActiveClientCount; index += 1) {
              indexes.add(index);
            }
            return [...indexes].sort((leftIndex, rightIndex) => leftIndex - rightIndex);
          });
        } else {
          setRetiringClientIndexes((currentIndexes) =>
            currentIndexes.filter((index) => index >= selectedClientCount)
          );
        }

        emitPageLoadTestEvent("client_count_applied", {
          targetClientCount: selectedClientCount,
          previousActiveClientCount: currentActiveClientCount,
        });
        return selectedClientCount;
      });
    }, CLIENT_COUNT_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [emitPageLoadTestEvent, selectedClientCount]);

  return (
    <Stack
      spacing={2}
      sx={{
        p: { xs: 2, md: 3 },
        height: fillAvailableSpace ? { md: "calc(100vh - 96px)" } : "auto",
        minHeight: { md: 720 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <ScoutIcon sx={{ width: 36, height: 36 }} />
          <Box minWidth={0}>
            <Typography variant="h4" fontWeight={600}>
              Scout Load Test
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tenant ? `Generating load for ${tenant.name}` : "Select an organization to start."}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Tooltip
            title={tokenRenderingDisabledForClientCount ? TOKEN_RENDERING_DISABLED_TOOLTIP : ""}
          >
            <span>
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveRenderTokens}
                    disabled={tokenRenderingDisabledForClientCount}
                    onChange={(event) => setRenderTokens(event.target.checked)}
                  />
                }
                disabled={tokenRenderingDisabledForClientCount}
                label="Render tokens"
              />
            </span>
          </Tooltip>
          <Box sx={{ minWidth: { xs: "100%", sm: 220 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography id="scout-load-test-client-count-label" variant="body2">
                Clients
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedClientCount}
                {clientCountIsPending ? " pending" : ""}
              </Typography>
            </Stack>
            <Slider
              aria-labelledby="scout-load-test-client-count-label"
              min={MIN_LOAD_TEST_CLIENT_COUNT}
              max={MAX_LOAD_TEST_CLIENT_COUNT}
              step={10}
              marks={[
                { value: MIN_LOAD_TEST_CLIENT_COUNT, label: String(MIN_LOAD_TEST_CLIENT_COUNT) },
                { value: 500, label: "500" },
                { value: MAX_LOAD_TEST_CLIENT_COUNT, label: String(MAX_LOAD_TEST_CLIENT_COUNT) },
              ]}
              value={selectedClientCount}
              onChange={handleClientCountSelected}
              valueLabelDisplay="auto"
            />
          </Box>
          <Button variant="outlined" onClick={() => setTenantPickerOpen(true)}>
            Change Organization
          </Button>
        </Stack>
      </Stack>

      {!tenantId && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">
              Select an organization before starting the load test.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scout conversations require a tenant context. The test will start automatically after
              a staff organization is selected.
            </Typography>
            <Button variant="contained" onClick={() => setTenantPickerOpen(true)}>
              Select Organization
            </Button>
          </Stack>
        </Paper>
      )}

      {tenantId && !featureFlagsLoading && !scoutLoadTestEnabled && (
        <Alert severity="warning">
          Turn on the scout-load-test feature flag for this organization before starting the load
          test. Enabling it causes Scout conversations for this organization to use the fake
          in-memory provider in the Scout node.
        </Alert>
      )}

      {loadTestTenantId && (
        <Box
          sx={{
            flex: 1,
            minHeight: fillAvailableSpace ? 0 : "auto",
            display: "grid",
            gap: 2,
            alignContent: fillAvailableSpace ? "stretch" : "start",
            gridTemplateColumns: effectiveRenderTokens
              ? { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }
              : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
            gridTemplateRows: fillAvailableSpace ? { md: "repeat(2, minmax(0, 1fr))" } : undefined,
          }}
        >
          {renderedClientIndexes.map((index) => (
            <ScoutLoadTestQuadrant
              key={`${loadTestTenantId}-${index}`}
              index={index}
              tenantId={loadTestTenantId}
              renderTokens={effectiveRenderTokens}
              fillAvailableSpace={fillAvailableSpace}
              retiring={retiringClientIndexes.includes(index)}
              onRetired={handleClientRetired}
            />
          ))}
        </Box>
      )}

      <TenantPickerDialog
        open={tenantPickerOpen}
        onClose={() => setTenantPickerOpen(false)}
        onTenantSelected={handleTenantSelected}
      />
    </Stack>
  );
}
